import { LockOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { Tooltip } from 'components/ui';
import { Theme } from 'utils/color/horizonTheme';
import { SKYSPECS_BRAND_ICON_WHITE, SKYSPECS_BRAND_LOGO_WHITE } from 'utils/constants';
import { APP_VERSION } from 'utils/env';
import { getItem as getLocalStorageItem, setItem as setLocalStorageItem } from 'utils/storage';

import {
  Image,
  Logo,
  PrivacyPolicyLink,
  SidebarFooter,
  SidebarMeta,
  StyledMenu,
  StyledSider,
  VersionLabel,
} from './SideNav.style';
import { type Tab } from './types';

const sidebarCollapsedStorageKey = 'sidebar-collapsed';

interface Props {
  tabs: Tab[];
}

export const SideNav = ({ tabs }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<boolean | undefined>(
    () => getLocalStorageItem(sidebarCollapsedStorageKey) === 'true'
  );
  const { pathname } = useLocation();
  const useWhiteLogo = theme.themeName === Theme.Dark;
  // The rail is solid navy, so it always uses the white brand art: the full logo
  // when expanded, and the brand icon when collapsed.
  const sideNavLogoSrc = collapsed ? SKYSPECS_BRAND_ICON_WHITE : SKYSPECS_BRAND_LOGO_WHITE;

  const activeTab = tabs
    .filter(({ link, subRoutes = [] }) => {
      const routes = [link, ...subRoutes];
      return !isEmpty(routes.filter(route => !!route && pathname.startsWith(route)));
    })
    .map(({ id }) => id);

  const handleCollapse = (collapsed: boolean, type: 'clickTrigger' | 'responsive') => {
    // Save in local storage if this was a user action (i.e. not responsive behavior)
    if (type === 'clickTrigger') {
      setLocalStorageItem(sidebarCollapsedStorageKey, String(collapsed));
    }

    // Skip reopening when window is expanded and user has collapsed the sidebar previously
    if (
      type === 'responsive' &&
      collapsed === false &&
      getLocalStorageItem(sidebarCollapsedStorageKey) === 'true'
    ) {
      return;
    }
    setCollapsed(prevState => !prevState);
  };

  return (
    <StyledSider
      breakpoint="lg"
      collapsed={collapsed}
      data-testid="sidebar-nav"
      collapsible
      onCollapse={handleCollapse}
      $styledComponentsTheme={theme}
    >
      <Logo>
        <Link to="/" data-testid="sidebar-logo">
          <Image
            $collapsed={collapsed}
            $invertForNavyRail={collapsed && useWhiteLogo}
            alt={t('navigation.skyspecsLogo', 'SkySpecs Logo')}
            src={sideNavLogoSrc}
          />
        </Link>
      </Logo>
      <StyledMenu
        selectedKeys={activeTab}
        style={{
          padding: collapsed ? '1em 0' : '1em',
        }}
        items={tabs.map(
          ({ id, title, icon, link, target, disabled, disabledTooltip, noLinkTooltip }) => {
            // Allow components to soft-reload themselves if navigating to the current path
            const isSelfLink = link === pathname;
            return {
              label: disabled ? (
                <Tooltip title={disabledTooltip} placement="right">
                  {title}
                </Tooltip>
              ) : link ? (
                <Link
                  replace={isSelfLink}
                  to={{ pathname: link, state: { softReload: isSelfLink } }}
                  target={target}
                >
                  {title}
                </Link>
              ) : noLinkTooltip ? (
                <Tooltip title={noLinkTooltip} placement="right">
                  {title}
                </Tooltip>
              ) : (
                title
              ),
              icon,
              disabled,
              key: id,
              'data-testid': id,
            };
          }
        )}
      />
      <SidebarMeta>
        <VersionLabel $collapsed={collapsed} data-testid="app-version">
          {collapsed ? APP_VERSION : `Version ${APP_VERSION}`}
        </VersionLabel>
        <SidebarFooter $collapsed={collapsed}>
          <Tooltip
            title={collapsed ? t('navigation.privacyPolicy', 'Privacy Policy') : undefined}
            placement="right"
          >
            <PrivacyPolicyLink
              aria-label={collapsed ? t('navigation.privacyPolicy', 'Privacy Policy') : undefined}
              target="_blank"
              href="https://skyspecs.com/privacy/"
              rel="noopener noreferrer"
            >
              <LockOutlined />
              {!collapsed ? t('navigation.privacyPolicy', 'Privacy Policy') : null}
            </PrivacyPolicyLink>
          </Tooltip>
        </SidebarFooter>
      </SidebarMeta>
    </StyledSider>
  );
};
